// components/pages/Duyurular/Duyurular.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Card,
    CardContent,
    Divider,
    styled,
    Grid,
    Chip,
    Avatar,
    Skeleton,
    Badge,
    IconButton,
    Stack,
    Tooltip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import { getDuyurular } from '../../../services/api/duyuruService';
import { DuyuruError } from '../../../types/ErrorModals/DuyuruError';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1),
    },
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1),
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
        backgroundColor: theme.palette.primary.light + '20',
        transition: 'background-color 0.3s ease',
    },
    transition: 'background-color 0.2s',
}));

const AnnouncementCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[3],
    transition: 'transform 0.3s, box-shadow 0.3s',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[8],
    },
}));

const CardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
}));

const CardFooter = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const Duyurular: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { kullaniciModal } = useAuth();
    
    const [selectedSeason, setSelectedSeason] = useState<string>(String(getCurrentSezon()));
    const availableSeasons = getSezonOptions().map(String);
    
    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };
    
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['duyurular', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getDuyurular({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogrenciId: kullaniciModal?.id || '',
                    sezon: parseInt(selectedSeason)
                });
            } catch (err) {
                const duyuruError = err as DuyuruError;
                throw new Error(duyuruError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });
    
    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);
    
    // Render skeletons for better UX during loading
    const renderSkeletons = () => {
        if (isTablet) {
            return (
                <Grid container spacing={2}>
                    {[1, 2, 3].map((item) => (
                        <Grid item xs={12} key={item}>
                            <Card sx={{ borderRadius: theme.shape.borderRadius * 1.5 }}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.primary.main }}>
                                    <Skeleton variant="text" width="60%" height={32} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                </Box>
                                <CardContent>
                                    <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
                                    <Skeleton variant="rectangular" height={1} sx={{ my: 1.5 }} />
                                    <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" height={20} width="80%" />
                                </CardContent>
                                <Box sx={{ p: 1.5, bgcolor: theme.palette.grey[100] }}>
                                    <Skeleton variant="text" width="30%" height={24} />
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            );
        }
        
        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Tarih', 'Saat', 'Başlık', 'İçerik', 'Gönderen'].map((header) => (
                                <TableCell key={header} sx={{ backgroundColor: theme.palette.primary.main }}>
                                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[1, 2, 3, 4].map((row) => (
                            <TableRow key={row}>
                                {[1, 2, 3, 4, 5].map((cell) => (
                                    <TableCell key={cell}>
                                        <Skeleton variant="text" />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };
    
    // Enhanced card view for announcements
    const renderCardView = () => {
        return (
            <Grid container spacing={2}>
                {data?.map((duyuru, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <AnnouncementCard>
                            <CardHeader>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <NotificationsIcon />
                                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                        {duyuru.aciklama}
                                    </Typography>
                                </Box>
                            </CardHeader>
                            
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Chip 
                                        icon={<CalendarTodayIcon />} 
                                        label={format(new Date(duyuru.tarih2 || new Date()), 'dd MMMM yyyy', { locale: tr })}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip 
                                        icon={<AccessTimeIcon />} 
                                        label={duyuru.displaySaat}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                </Box>
                                
                                <Divider sx={{ my: 1.5 }} />
                                
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mb: 2,
                                        minHeight: '60px',
                                        maxHeight: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical',
                                    }}
                                >
                                    {duyuru.aciklama2 || "Detaylı açıklama bulunmamaktadır."}
                                </Typography>
                            </CardContent>
                            
                            <CardFooter>
                                <Tooltip title="Gönderen">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar 
                                            sx={{ 
                                                width: 32, 
                                                height: 32, 
                                                bgcolor: theme.palette.primary.main,
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {duyuru.gorusen?.charAt(0) || 'G'}
                                        </Avatar>
                                        <Typography variant="body2" color="text.secondary">
                                            {duyuru.gorusen}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            </CardFooter>
                        </AnnouncementCard>
                    </Grid>
                ))}
            </Grid>
        );
    };
    
    // Enhanced table view for announcements
    const renderTableView = () => {
        return (
            <TableContainer 
                component={Paper} 
                elevation={3} 
                sx={{ 
                    minWidth: 800, 
                    borderRadius: theme.shape.borderRadius * 1.5,
                    overflow: 'hidden'
                }}
            >
                <Table aria-label="duyurular tablosu">
                    <TableHead>
                        <TableRow>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CalendarTodayIcon sx={{ mr: 1 }} />
                                    Tarih
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <AccessTimeIcon sx={{ mr: 1 }} />
                                    Saat
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <TitleIcon sx={{ mr: 1 }} />
                                    Başlık
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DescriptionIcon sx={{ mr: 1 }} />
                                    İçerik
                                </Box>
                            </HeaderTableCell>
                            <HeaderTableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PersonIcon sx={{ mr: 1 }} />
                                    Gönderen
                                </Box>
                            </HeaderTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((duyuru, index) => (
                            <StyledTableRow key={index}>
                                <StyledTableCell>
                                    <Chip 
                                        label={format(new Date(duyuru.tarih2 || new Date()), 'dd MMMM yyyy', { locale: tr })}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        icon={<CalendarTodayIcon />}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip 
                                        label={duyuru.displaySaat}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        icon={<AccessTimeIcon />}
                                    />
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="medium" 
                                        sx={{ 
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {duyuru.aciklama}
                                    </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            maxWidth: 300,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {duyuru.aciklama2 || "Detaylı açıklama bulunmamaktadır."}
                                    </Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar 
                                            sx={{ 
                                                width: 24, 
                                                height: 24, 
                                                bgcolor: theme.palette.primary.main,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {duyuru.gorusen?.charAt(0) || 'G'}
                                        </Avatar>
                                        <Typography variant="body2">
                                            {duyuru.gorusen}
                                        </Typography>
                                    </Box>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };
    
    // Calculate statistics
    const calculateStats = () => {
        if (!data) return { total: 0, today: 0, thisWeek: 0 };
        
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        
        const todayCount = data.filter(duyuru => {
            const duyuruDate = new Date(duyuru.tarih2 || new Date());
            return duyuruDate >= today;
        }).length;
        
        const weekCount = data.filter(duyuru => {
            const duyuruDate = new Date(duyuru.tarih2 || new Date());
            return duyuruDate >= weekStart;
        }).length;
        
        return {
            total: data.length,
            today: todayCount,
            thisWeek: weekCount
        };
    };
    
    const stats = calculateStats();
    
    return (
        <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3
            }}>
                <Typography 
                    variant="h5" 
                    component="h1"
                    sx={{ 
                        fontWeight: 600, 
                        mb: { xs: 2, sm: 0 },
                        color: theme.palette.text.primary
                    }}
                >
                    <AnnouncementIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Duyurular
                </Typography>
                
                <SeasonSelector
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                    <Select
                        labelId="season-select-label"
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        label="Eğitim Sezonu"
                    >
                        {availableSeasons.map((season) => (
                            <MenuItem key={season} value={season}>
                                {parseInt(season) - 1} - {season} Eğitim Yılı
                            </MenuItem>
                        ))}
                    </Select>
                </SeasonSelector>
            </Box>
            
            {/* Statistics Cards - Only show when data is available */}
            {!isLoading && !error && data && data.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={3} sx={{ 
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                    <NotificationsIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
                                    <Typography variant="body2" color="text.secondary">Toplam Duyuru</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <Card elevation={3} sx={{ 
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                    <CalendarTodayIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.today}</Typography>
                                    <Typography variant="body2" color="text.secondary">Bugünkü Duyurular</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <Card elevation={3} sx={{ 
                            height: '100%',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: theme.shadows[6],
                            },
                        }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                                    <CalendarTodayIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.thisWeek}</Typography>
                                    <Typography variant="body2" color="text.secondary">Bu Haftaki Duyurular</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
            
            {isLoading ? (
                <Box sx={{ mt: 3 }}>
                    {renderSkeletons()}
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    variant="filled"
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[3]
                    }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                                CorrelationId: {(error as DuyuruError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert 
                    severity="info" 
                    variant="outlined"
                    icon={<AnnouncementIcon />}
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Typography variant="body1">
                        {parseInt(selectedSeason) - 1} - {selectedSeason} eğitim sezonunda kayıtlı duyuru bulunmamaktadır.
                    </Typography>
                </Alert>
            ) : (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    {isTablet ? renderCardView() : renderTableView()}
                </Box>
            )}
        </Box>
    );
};

export default Duyurular;